import React from 'react';
import { Badge, Button, FormField, Popover, Select, SelectProps } from '@amzn/awsui-components-react';
import { ReportVersion } from 'src/models/reports/ReportVersions';
import {
    FILTER_LABELS,
    GENERATE_BUTTON_LABELS,
    GENERATE_BUTTON_TOOLTIPS,
} from './tpTaxSectionConstants';

type ComponentProps = {
    // Accounting-period selector.
    periodOptions: SelectProps.Option[];
    periodsLoading: boolean;
    selectedPeriod: string;
    onPeriodChange: (period: string) => void;
    // Report-version selector (built from the period's versions; in-progress versions are disabled).
    versions: ReportVersion[];
    selectedVersionId?: string;
    onVersionChange: (versionId: string) => void;
    versionsLoading: boolean;
    // Whether the current user may run the admin-only Full Refresh (reuses the reporting edit permission).
    canGenerate: boolean;
    // Opens the Full Refresh confirmation for the selected period.
    onFullRefresh: () => void;
    // Disables the Full Refresh button (no period selected, or a refresh is already in flight / running).
    fullRefreshDisabled: boolean;
};

const toPeriodOption = (period: string): SelectProps.Option => ({ label: period, value: period });

/**
 * Filter bar for the redesigned TP Tax Reports section: Accounting Period + Report Version selectors
 * and the admin-gated Full Refresh action (with a tooltip). Full Refresh triggers an on-demand full
 * report refresh for the selected period via the orchestrator. (A Quick VAR + PLI Refresh action will
 * be added later when that lighter refresh path is built.)
 */
export default function ReportFilterBar(props: ComponentProps) {
    const selectedPeriodOption = props.selectedPeriod ? toPeriodOption(props.selectedPeriod) : null;

    const versionOptions: SelectProps.Option[] = props.versions.map((v) => ({
        label: v.versionLabel,
        value: v.jobId,
        // In-progress runs are shown but not selectable (the report isn't ready yet). Surface an inline
        // hourglass icon + description so the user understands WHY the option is greyed out, rather than
        // it just looking disabled for no reason.
        disabled: v.inProgress,
        ...(v.inProgress
            ? { iconName: 'status-in-progress' as const, description: 'Generation in progress — not yet available' }
            : {}),
    }));
    const selectedVersionOption =
        versionOptions.find((o) => o.value === props.selectedVersionId) ?? null;

    // A flex row bottom-aligned (align-items: flex-end) so the label-less Full Refresh button lines up
    // with the *controls* of the labeled Selects, not their label row. SpaceBetween top-aligns its
    // children, which left the button straddling the label + control rows.
    return (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap' }}>
            <FormField label={FILTER_LABELS.ACCOUNTING_PERIOD}>
                <Select
                    selectedOption={selectedPeriodOption}
                    options={props.periodOptions}
                    onChange={({ detail }) => props.onPeriodChange(detail.selectedOption.value ?? '')}
                    statusType={props.periodsLoading ? 'loading' : 'finished'}
                    loadingText="Loading periods"
                    placeholder="Select a period"
                    data-testid="report-period-select"
                />
            </FormField>

            <FormField label={FILTER_LABELS.REPORT_VERSION}>
                <Select
                    selectedOption={selectedVersionOption}
                    options={versionOptions}
                    onChange={({ detail }) => props.onVersionChange(detail.selectedOption.value ?? '')}
                    statusType={props.versionsLoading ? 'loading' : 'finished'}
                    loadingText="Loading report versions"
                    empty="No reports available for this period"
                    placeholder="Select a report version"
                    data-testid="report-version-select"
                />
            </FormField>

            {props.canGenerate && (
                <Popover
                    dismissButton={false}
                    position="bottom"
                    triggerType="custom"
                    content={GENERATE_BUTTON_TOOLTIPS.FULL_REFRESH}
                >
                    <Button
                        variant="normal"
                        data-testid="full-refresh-button"
                        disabled={props.fullRefreshDisabled}
                        onClick={props.onFullRefresh}
                    >
                        {GENERATE_BUTTON_LABELS.FULL_REFRESH} <Badge color="red">Admin</Badge>
                    </Button>
                </Popover>
            )}
        </div>
    );
}
